import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import PageHero from '../components/layout/PageHero';
import { usePageSeo } from '../hooks/usePageSeo';
import { ALL_TOOLS } from '../lib/toolsCatalog';
import { SIMULATOR_CATALOG } from '../lib/simulationCatalog';
import { useHowItWorksTopics } from '../lib/howItWorksHierarchy';
import { useArticleSections } from '../lib/articlesHierarchy';
import {
  GraduationCap,
  Wrench,
  Users2,
  BookOpen,
  Lightbulb,
  Calculator,
  Activity,
  MessageSquare,
  FileCheck2,
  Sigma,
  Gauge,
  ShieldCheck,
  ArrowRight,
  Mail,
} from 'lucide-react';

const PAGE_TITLE = 'About AUTOXIUM | Automotive Engineering Knowledge Platform';
const PAGE_DESCRIPTION =
  'AUTOXIUM is an independent automotive engineering platform: concept explainers, technical articles, engineering calculators and simulators for EV, diagnostics and vehicle networks.';

const CONTACT_EMAIL = 'contact@autoxium.com';

const audiences = [
  {
    icon: GraduationCap,
    title: 'Students & Newcomers',
    description: 'Learning automotive engineering fundamentals and looking for a structured, technical starting point.',
  },
  {
    icon: Wrench,
    title: 'Working Engineers',
    description: 'Diagnosing real systems and needing precise references on protocols, standards, and calculations.',
  },
  {
    icon: Users2,
    title: 'Enthusiasts & Contributors',
    description: 'Hands-on tinkerers and hobbyists who want to go deeper than forum folklore, and share what they learn.',
  },
];

const principles = [
  {
    icon: FileCheck2,
    title: 'Standards-Referenced',
    description:
      'Content and calculations are grounded in published standards and recognised engineering references — such as ISO 14229 (UDS), SAE J1939, IEC 60364 and ISO 26262 — and cite them where they apply.',
  },
  {
    icon: Sigma,
    title: 'Transparent Methods',
    description:
      'Every tool states its formulas, assumptions and limits, and ships with a user guide. You can see how a result was reached, not just the number.',
  },
  {
    icon: Gauge,
    title: 'Checked Against Reality',
    description:
      'Models are sanity-checked against published data and real vehicles, and revised when they fall short. Known gaps are documented rather than hidden.',
  },
  {
    icon: ShieldCheck,
    title: 'Free & Privacy-Respecting',
    description:
      'Reading and using the platform is free. We run no advertising trackers or third-party analytics, and store only what is essential for the site to work.',
  },
];

const coverageAreas = [
  'E-Powertrain, Battery & BMS',
  'Charging & Power Electronics',
  'Electric Motors & Controls',
  'Chassis & Vehicle Dynamics',
  'Body Electronics & Comfort',
  'Infotainment & Connectivity',
  'E/E Architecture & Vehicle Networks',
  'ADAS & Automated Driving',
  'Diagnostics (UDS, DTCs, Security Access)',
  'AUTOSAR',
  'Calibration & Measurement',
  'Systems Engineering & Requirements',
  'Functional Safety & Cybersecurity',
];

const SectionHeading = ({ eyebrow, title, children }) => (
  <div className="max-w-3xl mb-8">
    {eyebrow && (
      <p className="font-mono text-xs tracking-widest text-autoxium-blue uppercase mb-3">{eyebrow}</p>
    )}
    <h2 className="font-chivo text-2xl md:text-3xl font-bold uppercase tracking-tight text-chassis">{title}</h2>
    {children && <p className="mt-3 text-muted-foreground leading-relaxed">{children}</p>}
  </div>
);

const AboutPage = () => {
  usePageSeo(PAGE_TITLE, PAGE_DESCRIPTION);
  const { topics, loading: topicsLoading } = useHowItWorksTopics();
  const { articles, loading: articlesLoading } = useArticleSections();

  // Counts come from the same catalogs/APIs the listing pages use, so they never go stale.
  const offerings = useMemo(
    () => [
      {
        icon: Lightbulb,
        title: 'How Does It Work?',
        to: '/how-it-works',
        count: topicsLoading ? null : topics.length,
        noun: 'topic',
        description:
          'Concise, diagram-led explanations of how individual vehicle systems work — from BMS cell balancing to CAN arbitration and ADAS sensor fusion.',
      },
      {
        icon: BookOpen,
        title: 'Articles',
        to: '/articles',
        count: articlesLoading ? null : articles.length,
        noun: 'article',
        description:
          'Long-form technical deep dives into ECUs and engineering processes — traction inverters, BMS, DC-DC converters, functional safety and cybersecurity.',
      },
      {
        icon: Calculator,
        title: 'Engineering Tools',
        to: '/tools',
        count: ALL_TOOLS.length,
        noun: 'tool',
        description:
          'Browser-based calculators and decoders for everyday work: cable, fuse and busbar sizing, CAN/J1939 decoding, UDS diagnostics, EV sizing and unit conversion.',
      },
      {
        icon: Activity,
        title: 'Simulation',
        to: '/simulation',
        count: SIMULATOR_CATALOG.length,
        noun: 'simulator',
        description:
          'Interactive simulators for EV range and performance, charging, regenerative braking and battery thermal behaviour over standard drive cycles.',
      },
      {
        icon: MessageSquare,
        title: 'Community',
        to: '/community',
        count: null,
        description:
          'A technical Q&A forum where engineers, students and enthusiasts ask questions, share answers and vote on the most useful ones.',
      },
    ],
    [topics, topicsLoading, articles, articlesLoading]
  );

  return (
    <div className="min-h-screen" data-testid="about-page">
      <PageHero
        eyebrow="About Us"
        title="About AUTOXIUM"
        description="An independent automotive engineering platform that explains how modern vehicles work — and gives you the tools to calculate, simulate and verify it yourself."
      />

      {/* Mission & story */}
      <section className="py-12 md:py-16">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8">
          <div className="max-w-3xl prose-autoxium">
            <h2>Our Mission</h2>
            <p>
              Automotive engineering knowledge is scattered across dense standards documents,
              paywalled textbooks, and forum threads that go stale in a year. AUTOXIUM exists to
              bring the technical core of that knowledge — systems, protocols, diagnostics,
              calculations and hands-on practice — into one place that is current, accurate and
              free to use.
            </p>
            <h2>Why AUTOXIUM</h2>
            <p>
              Vehicles have become software-defined, electrified and networked systems. Understanding
              them now means connecting battery chemistry to power electronics, CAN frames to
              diagnostic services, and drive cycles to range. AUTOXIUM is built around that
              system-level view: each concept explainer links to the tools and simulators that let
              you put it into practice, so learning and doing happen side by side.
            </p>
          </div>
        </div>
      </section>

      {/* What you'll find */}
      <section className="py-12 md:py-16 bg-white border-y border-structure">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8">
          <SectionHeading eyebrow="The Platform" title="What You'll Find">
            Five connected sections, each designed for a different way of learning and working.
          </SectionHeading>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="about-offerings">
            {offerings.map(({ icon: Icon, title, to, count, noun, description }) => (
              <Link
                key={title}
                to={to}
                className="technical-card rounded-xl p-6 flex flex-col group"
                data-testid={`about-offering-${to.slice(1)}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="h-10 w-10 rounded-md bg-chassis flex items-center justify-center">
                    <Icon className="h-5 w-5 text-autoxium-blue" />
                  </div>
                  {count != null && (
                    <span className="font-mono text-xs text-muted-foreground">
                      {count} {noun}{count !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                <h3 className="font-chivo font-bold uppercase tracking-tight mb-2">{title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed flex-1">{description}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-autoxium-blue">
                  Explore <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </span>
              </Link>
            ))}
            <div className="rounded-xl p-6 border border-dashed border-structure flex flex-col justify-center">
              <p className="font-mono text-xs tracking-widest text-autoxium-blue uppercase mb-2">Coming Soon</p>
              <h3 className="font-chivo font-bold uppercase tracking-tight mb-2">Courses</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Structured learning paths that tie topics, tools and simulators together into guided courses.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="py-12 md:py-16">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8">
          <SectionHeading eyebrow="How We Work" title="Our Principles">
            The standards we hold our own content and tools to.
          </SectionHeading>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {principles.map(({ icon: Icon, title, description }) => (
              <div key={title} className="technical-card rounded-xl p-6">
                <div className="h-10 w-10 rounded-md bg-chassis flex items-center justify-center mb-4">
                  <Icon className="h-5 w-5 text-autoxium-blue" />
                </div>
                <h3 className="font-chivo font-bold uppercase tracking-tight mb-2">{title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Coverage */}
      <section className="py-12 md:py-16 bg-white border-y border-structure">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8">
          <SectionHeading eyebrow="Coverage" title="What We Cover">
            Content is organised by vehicle domain, from the high-voltage powertrain to the diagnostic stack.
          </SectionHeading>
          <ul className="flex flex-wrap gap-2" data-testid="about-coverage">
            {coverageAreas.map((area) => (
              <li
                key={area}
                className="rounded-full border border-structure bg-background px-4 py-1.5 text-sm text-chassis"
              >
                {area}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Audiences */}
      <section className="py-12 md:py-16">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8">
          <SectionHeading eyebrow="Audience" title="Who It's For" />
          <div className="grid md:grid-cols-3 gap-6">
            {audiences.map(({ icon: Icon, title, description }) => (
              <div key={title} className="technical-card rounded-xl p-6">
                <div className="h-10 w-10 rounded-md bg-chassis flex items-center justify-center mb-4">
                  <Icon className="h-5 w-5 text-autoxium-blue" />
                </div>
                <h3 className="font-chivo font-bold uppercase tracking-tight mb-2">{title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
              </div>
            ))}
          </div>

          {/* Independence & disclaimer */}
          <div className="mt-12 max-w-3xl rounded-xl border border-structure bg-white p-6" data-testid="about-disclaimer">
            <h3 className="font-chivo font-bold uppercase tracking-tight mb-2">Independence &amp; Use of Content</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              AUTOXIUM is independently owned and operated. It is not affiliated with, sponsored or
              endorsed by any vehicle manufacturer, supplier or standards body; product names,
              trademarks and standard designations belong to their respective owners and are used
              for reference only. Content and tool results are provided for educational and
              preliminary engineering purposes — always verify safety-critical or production designs
              against the official standards and with a qualified engineer. See our{' '}
              <Link to="/terms-of-service" className="text-autoxium-blue hover:underline">Terms of Service</Link>{' '}
              for details.
            </p>
          </div>
        </div>
      </section>

      {/* Get involved */}
      <section className="bg-chassis text-white py-12 md:py-16">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8">
          <div className="max-w-3xl">
            <p className="font-mono text-xs tracking-widest text-autoxium-blue uppercase mb-3">Get Involved</p>
            <h2 className="font-chivo text-2xl md:text-3xl font-bold uppercase tracking-tight mb-4">
              Help Shape What We Build Next
            </h2>
            <p className="text-white/70 leading-relaxed mb-8">
              Missing a topic you need? Spotted an error? Requests and corrections directly decide
              what gets written, built and fixed next.
            </p>
            <div className="flex flex-col sm:flex-row flex-wrap gap-3">
              <Link
                to="/request-topic"
                className="inline-flex items-center justify-center gap-2 rounded-md bg-autoxium-blue px-5 py-2.5 text-sm font-semibold text-white hover:opacity-90 transition-opacity"
              >
                Request a Topic <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/community"
                className="inline-flex items-center justify-center gap-2 rounded-md border border-white/30 px-5 py-2.5 text-sm font-semibold text-white hover:bg-white/10 transition-colors"
              >
                Ask the Community
              </Link>
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                className="inline-flex items-center justify-center gap-2 rounded-md border border-white/30 px-5 py-2.5 text-sm font-semibold text-white hover:bg-white/10 transition-colors"
              >
                <Mail className="h-4 w-4" /> {CONTACT_EMAIL}
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
